// A popover panel that renders into document.body via a portal and positions
// itself with `position: fixed`, anchored to a trigger element. This lets
// popovers in the property panel (bindings, color pickers, …) overlay the
// whole screen instead of being clipped by the panel's `overflow: auto`
// scroll container.
//
// Usage:
//   const anchorRef = useRef<HTMLButtonElement | null>(null)
//   <button ref={anchorRef} onClick={() => setOpen(o => !o)}>…</button>
//   {open && (
//     <FloatingPanel anchorRef={anchorRef} align="right" width={280}
//                    onClose={() => setOpen(false)}>
//       …panel content…
//     </FloatingPanel>
//   )}

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

interface FloatingPanelProps {
  /** The trigger element the panel is anchored to. */
  anchorRef: RefObject<HTMLElement | null>
  /** Called on outside-click or Escape. */
  onClose: () => void
  children: ReactNode
  /** Align the panel's left or right edge to the anchor. Default 'left'. */
  align?: 'left' | 'right'
  /** Fixed width (px). When omitted the panel sizes to its content. */
  width?: number
  /** Preferred placement; flips if it would overflow the viewport. Default 'bottom'. */
  placement?: 'bottom' | 'top'
  zIndex?: number
}

export function FloatingPanel({
  anchorRef, onClose, children, align = 'left', width, placement = 'bottom', zIndex = 1000,
}: FloatingPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // Position against the anchor, re-running on scroll/resize so the panel
  // tracks the trigger. Measured after layout so panel dimensions are known.
  useLayoutEffect(() => {
    const update = () => {
      const a = anchorRef.current
      if (!a) return
      const r = a.getBoundingClientRect()
      const pw = width ?? (panelRef.current?.offsetWidth || 280)
      const ph = panelRef.current?.offsetHeight || 0
      const gap = 4
      let left = align === 'right' ? r.right - pw : r.left
      left = Math.max(8, Math.min(left, window.innerWidth - pw - 8))
      let top = placement === 'top' ? r.top - gap - ph : r.bottom + gap
      // Flip when the preferred placement overflows the viewport edge.
      if (placement !== 'top' && top + ph > window.innerHeight - 8) {
        const flipped = r.top - gap - ph
        if (flipped >= 8) top = flipped
      } else if (placement === 'top' && top < 8) {
        top = r.bottom + gap
      }
      setPos({ top, left })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [anchorRef, align, width, placement])

  // Dismiss on outside click (ignoring the anchor, which toggles itself) or Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return
      if (anchorRef.current?.contains(e.target as Node)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchorRef, onClose])

  return createPortal(
    <div
      ref={panelRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        zIndex,
        width,
        // Hidden until measured so it never flashes at the wrong spot.
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
